<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-row class="mb-3">
        <b-col v-for="n in 3" :key="n" md="4">
          <b-card>
            <b-skeleton width="60%" class="mb-2"/>
            <b-skeleton width="40%" height="1.5rem" class="mb-1"/>
            <b-skeleton width="50%"/>
          </b-card>
        </b-col>
      </b-row>
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </b-card>
    </div>

    <template v-else>
      <!-- Privacy Alert -->
      <b-alert v-if="benchmark.total_companies < 5" variant="warning" show>
        Industry comparison requires data from at least 5 farms. Currently showing data from {{ benchmark.total_companies || 0 }} farm(s). Add more farms or wait for platform growth.
      </b-alert>

      <!-- Summary Cards -->
      <b-row>
        <b-col md="4">
          <b-card class="text-center border-top-success" style="border-top: 4px solid #28c76f;">
            <h6 class="text-muted">Your Best Farm</h6>
            <h4 class="text-success">{{ benchmark.best_farm || '—' }}</h4>
            <small class="text-muted">FCR: {{ benchmark.best_farm_fcr || '—' }}</small>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center" style="border-top: 4px solid #7367f0;">
            <h6 class="text-muted">Industry Top 25%</h6>
            <h4 class="text-primary">FCR ≤ {{ benchmark.industry_top_25_fcr || '—' }}</h4>
            <small class="text-muted">Top quartile benchmark</small>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center" :class="fcrCompareClass" style="border-top: 4px solid;">
            <h6 class="text-muted">Your Avg vs Industry Avg</h6>
            <h4 :class="fcrCompareTextClass">
              {{ benchmark.your_avg_fcr || '—' }} vs {{ benchmark.industry_avg_fcr || '—' }}
            </h4>
            <b-badge :variant="fcrCompareBadge">{{ fcrCompareLabel }}</b-badge>
          </b-card>
        </b-col>
      </b-row>

      <!-- Your Farms Table -->
      <b-card title="Your Farms Performance">
        <div v-if="!farms.length" class="text-center py-4 text-muted">
          No farm data available.
        </div>
        <b-table
          v-else
          :items="farms"
          :fields="farmFields"
          striped
          hover
          responsive
        >
          <template #cell(avg_fcr)="data">
            <span :class="data.value < 1.75 ? 'text-success' : data.value <= 2.0 ? 'text-warning' : 'text-danger'">
              {{ data.value }}
            </span>
          </template>
          <template #cell(avg_mortality_percent)="data">
            <span :class="data.value < 3 ? 'text-success' : data.value <= 5 ? 'text-warning' : 'text-danger'">
              {{ data.value }}%
            </span>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="data.value === 'active' ? 'success' : 'secondary'">{{ data.value }}</b-badge>
          </template>
        </b-table>
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BCard, BRow, BCol, BTable, BBadge, BSpinner, BAlert } from 'bootstrap-vue'

export default {
  name: 'BenchmarkingDashboard',
  components: { BCard, BRow, BCol, BTable, BBadge, BSpinner, BAlert },
  data() {
    return {
      loading: false,
      benchmark: {},
      farms: [],
      farmFields: [
        { key: 'farm_name', label: 'Farm Name', sortable: true },
        { key: 'avg_fcr', label: 'Avg FCR', sortable: true },
        { key: 'avg_mortality_percent', label: 'Avg Mortality %', sortable: true },
        { key: 'status', label: 'Status' },
      ],
    }
  },
  computed: {
    yourFcr() { return parseFloat(this.benchmark.your_avg_fcr) || null },
    industryFcr() { return parseFloat(this.benchmark.industry_avg_fcr) || null },
    fcrCompareClass() {
      if (!this.yourFcr || !this.industryFcr) return ''
      return this.yourFcr <= this.industryFcr ? 'border-success' : 'border-danger'
    },
    fcrCompareTextClass() {
      if (!this.yourFcr || !this.industryFcr) return 'text-muted'
      return this.yourFcr <= this.industryFcr ? 'text-success' : 'text-danger'
    },
    fcrCompareBadge() {
      if (!this.yourFcr || !this.industryFcr) return 'secondary'
      return this.yourFcr <= this.industryFcr ? 'success' : 'danger'
    },
    fcrCompareLabel() {
      if (!this.yourFcr || !this.industryFcr) return 'No data'
      return this.yourFcr <= this.industryFcr ? 'Better than Industry' : 'Below Industry Avg'
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const { data } = await axios.get('web/benchmark')
        const payload = data.data || data
        this.benchmark = payload
        this.farms = payload.company_farms || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load benchmarking data', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped></style>
